import {
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import { AxiosInstance } from 'axios';
import { Store } from 'react-notifications-component';

import {
  ProgressionHandler,
  uploadDocToAnalyse,
} from '../../services/file-upload';
import { createid } from '../../services/id';
import { addNotification } from '../../services/notification';
import { RootState } from '../store';
import {
  DAChat,
  DAChatId,
  DADataTable,
  DADataTableId,
  DADataTableTemplate,
  DASavedQuestion,
  DASavedQuestionId,
  ExtractionStatus,
  FileUploadStats,
  IChatState,
  PDFViewerInfo,
  ProgressActionPayload,
  WithUploadId,
} from './chat.types';

export const uploadDAFileThunk = createAsyncThunk<
  void,
  {
    file: File;
    axios: AxiosInstance;
    chatId: DAChatId;
    onUpdateAfterUpload: () => void;
  },
  {
    rejectValue: string;
  }
>(
  'uploadDAFile',
  async (
    { file, axios: _axios, chatId, onUpdateAfterUpload },
    { dispatch },
  ) => {
    const { name } = file;
    const uploadid = createid();

    const onProgress: ProgressionHandler = (progress) =>
      dispatch(setUploadProgress({ progress, uploadid }));

    const { promise } = uploadDocToAnalyse(file, _axios, chatId, onProgress);

    dispatch(addDAUpload({ uploadid, name, progress: 0 }));

    try {
      await promise;
      dispatch(removeUpload({ uploadid }));
      onUpdateAfterUpload();
    } catch (e) {
      if (e instanceof Error) {
        addNotification(
          Store,
          `An error occured while analysing ${name}`,
          e.message,
          'danger',
        );
        dispatch(removeUpload({ uploadid }));
      } else {
        console.log('Unexpected error', e);
      }
    }
  },
);

export const initialState: IChatState = {
  chats: {},
  activeChat: undefined,
  uploads: {},
  uploaded: {},
  dataTables: [],
  dataTablesTemplates: [],
  PDFViewerInfo: undefined,
  savedQuestions: [],
};

export const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    setUploadProgress(
      state,
      { payload: { uploadid, progress } }: PayloadAction<ProgressActionPayload>,
    ) {
      const uploaded = state.uploads[uploadid];
      if (!uploaded) return;
      uploaded.progress = progress;
    },
    setChats(state, { payload }: PayloadAction<DAChat[]>) {
      if (payload) {
        const chats: Record<DAChatId, DAChat> = {};
        payload
          .sort(function (a, b) {
            return Date.parse(b.date) - Date.parse(a.date);
          })
          .map((n) => {
            chats[n.id] = {
              id: n.id,
              name: n.name,
              date: n.date,
              status: n.status,
              files: [],
              messages: [],
            };
          });
        if (payload.length === Object.keys(state.chats).length + 1) {
          state.chats = chats;
          state.activeChat = Object.keys(chats)[0];
        }
        if (payload.length === Object.keys(state.chats).length - 1) {
          state.chats = chats;
          state.activeChat = undefined;
        }
        state.chats = chats;
        if (state.activeChat === undefined)
          state.activeChat = Object.keys(chats)[0];
      } else {
        state.chats = {};
        state.activeChat = undefined;
      }
    },
    setActiveChat(state, { payload }: PayloadAction<DAChatId | undefined>) {
      state.activeChat = payload;
    },
    setChatStatus(
      state,
      {
        payload: { chatId, status },
      }: PayloadAction<{ chatId: DAChatId } & { status: ExtractionStatus }>,
    ) {
      state.chats[chatId].status = status;
    },
    setChatInfo(
      state,
      {
        payload: { chatId, info },
      }: PayloadAction<{ chatId: DAChatId } & { info: DAChat }>,
    ) {
      state.chats[chatId] = info;
    },
    addChat(state, { payload }: PayloadAction<DAChatId>) {
      // all of this to keep the order "last added on top of the list"
      const chats: Record<DAChatId, DAChat> = {};
      chats[payload] = {
        id: payload,
        name: '',
        date: '',
        status: ExtractionStatus.Done,
        files: [],
        messages: [],
      };
      Object.keys(state.chats).forEach((c) => (chats[c] = state.chats[c]));
      state.chats = chats;
      state.activeChat = payload;
    },
    deleteChatFromStore(state, { payload }: PayloadAction<DAChatId>) {
      delete state.chats[payload];
    },
    addDAUpload(state, { payload }: PayloadAction<FileUploadStats>) {
      state.uploads = {
        ...state.uploads,
        [payload.uploadid]: payload,
      };
    },
    removeUpload(
      state,
      { payload: { uploadid } }: PayloadAction<WithUploadId>,
    ) {
      delete state.uploads[uploadid];
    },
    removeAllUploads(state) {
      state.uploads = {};
    },
    removeAllUploaded(state) {
      state.uploaded = {};
    },
    setDataTables(state, { payload }: PayloadAction<DADataTable[]>) {
      if (payload) {
        const dataTables: DADataTable[] = [];
        payload.map((table) => {
          dataTables.push(table);
        });
        state.dataTables = dataTables;
      } else {
        state.dataTables = [];
      }
    },
    setDataTablesTemplates(
      state,
      { payload }: PayloadAction<DADataTableTemplate[]>,
    ) {
      if (payload) {
        const templates: DADataTableTemplate[] = [];
        payload.map((t) => {
          templates.push(t);
        });
        state.dataTablesTemplates = templates;
      } else {
        state.dataTablesTemplates = [];
      }
    },
    addDataTable(state, { payload }: PayloadAction<DADataTable>) {
      if (payload) {
        const dataTables: DADataTable[] = state.dataTables;
        dataTables.push(payload);
        state.dataTables = dataTables;
      }
    },
    deleteDataTable(state, { payload }: PayloadAction<DADataTableId>) {
      if (payload) {
        const dataTables: DADataTable[] = state.dataTables.filter(
          (t) => t.id !== payload,
        );
        state.dataTables = dataTables;
      }
    },
    setSavedQuestions(state, { payload }: PayloadAction<DASavedQuestion[]>) {
      if (payload) {
        const savedQuestions: DASavedQuestion[] = [];
        payload.map((sq) => {
          savedQuestions.push(sq);
        });
        state.savedQuestions = savedQuestions;
      } else {
        state.savedQuestions = [];
      }
    },
    addSavedQuestion(state, { payload }: PayloadAction<DASavedQuestion>) {
      if (payload) {
        const savedQuestions: DASavedQuestion[] = state.savedQuestions;
        savedQuestions.push(payload);
        state.savedQuestions = savedQuestions;
      }
    },
    deleteQuestion(state, { payload }: PayloadAction<DASavedQuestionId>) {
      if (payload) {
        const savedQuestions: DASavedQuestion[] = state.savedQuestions.filter(
          (t) => t.id !== payload,
        );
        state.savedQuestions = savedQuestions;
      }
    },
    setPDFViewerInfo(
      state,
      { payload }: PayloadAction<PDFViewerInfo | undefined>,
    ) {
      if (payload) {
        state.PDFViewerInfo = payload;
      } else {
        state.PDFViewerInfo = undefined;
      }
    },
  },
});

export const {
  setChats,
  setActiveChat,
  deleteChatFromStore,
  addDAUpload,
  setUploadProgress,
  removeUpload,
  removeAllUploads,
  removeAllUploaded,
  addChat,
  setChatStatus,
  setChatInfo,
  setDataTables,
  addDataTable,
  deleteDataTable,
  setDataTablesTemplates,
  setPDFViewerInfo,
  setSavedQuestions,
  addSavedQuestion,
  deleteQuestion,
} = chatSlice.actions;

export const chatSelector = (state: RootState): IChatState => state.chat;

export const selectChats = createSelector(chatSelector, (chat) => chat.chats);
export const selectActiveChat = createSelector(
  chatSelector,
  (chat) => chat.activeChat,
);
export const selectDAUploads = createSelector(chatSelector, (chat) =>
  chat.uploads === undefined ? [] : Object.values(chat.uploads),
);
export const selectDADataTables = createSelector(chatSelector, (chat) =>
  chat.dataTables === undefined ? [] : chat.dataTables,
);

export const selectDASavedQuestions = createSelector(chatSelector, (chat) =>
  chat.savedQuestions === undefined ? [] : chat.savedQuestions,
);

export const selectDADataTablesTemplates = createSelector(
  chatSelector,
  (chat) =>
    chat.dataTablesTemplates === undefined
      ? []
      : Object.values(chat.dataTablesTemplates),
);

export const selectPDFViewerInfo = createSelector(chatSelector, (chat) =>
  chat.PDFViewerInfo === undefined ? undefined : chat.PDFViewerInfo,
);
